import { Button } from '@/components/ui/button'
import { Content } from '../useContentData'
import { ChatInterface } from '@/components/chat/ChatInterface'
import ContentRenderer from '../renderers/ContentRenderer'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { ErrorAlert } from './alerts/ErrorAlert'

interface ImprovementPanelProps {
    content: Content
    versionNumber?: number
    error: string | null
    onVersionCreated: (versionNumber: number) => void
    onExitImproveMode: () => void
    chatInitialFocus?: boolean
    onChatFocused?: () => void
    onError?: (message: string) => void
}

/**
 * Enhanced split-panel component for content improvement with AI
 */
export function ImprovementPanel({
    content,
    versionNumber,
    error,
    onVersionCreated,
    onExitImproveMode,
    chatInitialFocus = false,
    onChatFocused,
    onError
}: ImprovementPanelProps) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20 relative overflow-hidden p-6">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            
            {error && <ErrorAlert message={error} />}
            
            <div className="relative">
                <ResizablePanelGroup
                    direction="horizontal"
                    className="h-[calc(100vh-12rem)] rounded-2xl border border-emerald-200/60 shadow-lg overflow-hidden"
                >
                    <ResizablePanel defaultSize={50} minSize={30}>
                        <div className="h-full bg-gradient-to-br from-emerald-50/80 via-green-50/60 to-white flex flex-col relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-transparent to-green-50/20" />
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
                            <div className="relative border-b border-emerald-100/50 p-6">
                                <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-800 to-green-700 bg-clip-text text-transparent mb-2">AI Assistant</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Chat with AI to get suggestions and improvements for your content.
                                </p>
                            </div>
                            <div className="flex-1 relative">
                                <ChatInterface
                                    contentId={content.id}
                                    onVersionCreated={onVersionCreated}
                                    autoFocus={chatInitialFocus}
                                    onFocused={onChatFocused}
                                    onError={onError}
                                />
                            </div>
                        </div>
                    </ResizablePanel>
                    
                    <ResizableHandle className="bg-gradient-to-b from-emerald-200/50 to-green-200/50 hover:from-emerald-300/60 hover:to-green-300/60 transition-all duration-300" />
                    
                    <ResizablePanel defaultSize={50} minSize={30}>
                        <div className="h-full bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-white flex flex-col relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
                            <div className="relative border-b border-blue-100/50 p-6">
                                <h3 className="text-xl font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent mb-2">Content Preview</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    See your content updates in real-time as you make improvements.
                                </p>
                            </div>
                            <div className="flex-1 relative">
                                <div className="h-full bg-white/80 backdrop-blur-sm">
                                    <ContentRenderer 
                                        content={content}
                                        versionNumber={versionNumber}
                                    />
                                </div>
                            </div>
                        </div>
                    </ResizablePanel>
                </ResizablePanelGroup>
                
                {/* Enhanced Exit Button */}
                <div className="flex justify-center mt-6">
                    <div className="group relative overflow-hidden bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/5" />
                        <Button
                            variant="default"
                            onClick={onExitImproveMode}
                            className="relative bg-transparent hover:bg-transparent text-white font-medium px-8 py-3 border-0 shadow-none"
                        >
                            Exit Improve Mode
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}